// Display-only helpers for the Market Explorer.
// NEVER mutate stored/computed data — these format numbers and localize country
// labels for the French UI. market_key and source data remain untouched.

// French number formatting (comma decimals, space thousands via fr-FR locale).
export const frNum = (n, d = 0) =>
  Number(n).toLocaleString("fr-FR", { minimumFractionDigits: d, maximumFractionDigits: d });

// Adaptive units by order of magnitude.
export function formatPassengers(raw) {
  const n = Number(raw) || 0;
  if (n >= 1e6) return { value: frNum(n / 1e6, 1), unit: "M" };
  if (n >= 1e3) return { value: frNum(n / 1e3, 0), unit: "k" };
  return { value: frNum(n, 0), unit: "" };
}

export function formatRevenue(raw) {
  const n = Number(raw) || 0;
  if (n >= 1e9) return { value: frNum(n / 1e9, 1), unit: "Md $" };
  if (n >= 1e6) return { value: frNum(n / 1e6, 0), unit: "M $" };
  return { value: frNum(n, 0), unit: "$" };
}

export function formatFare(raw) {
  return { value: frNum(Number(raw) || 0, 0), unit: "$" };
}

export const fmtPaxStr = (raw) => {
  const f = formatPassengers(raw);
  return f.unit ? `${f.value} ${f.unit}` : f.value;
};
// Ranking revenue keeps finer precision than the KPI card (Md $ 2 dec, M $ 1 dec)
// so FR-WORLD rankings stay identical while small markets read cleanly in M $.
export const fmtRevStr = (raw) => {
  const n = Number(raw) || 0;
  if (n >= 1e9) return `${frNum(n / 1e9, 2)} Md $`;
  if (n >= 1e6) return `${frNum(n / 1e6, 1)} M $`;
  return `${frNum(n, 0)} $`;
};

// Generic English-country-name -> French display table (extensible, ISO-friendly).
// Used only for UI labels; does not affect data or market_key.
export const COUNTRY_FR = {
  FRANCE: "France", FR: "France", MONDE: "Monde", WORLD: "Monde",
  // France -> Mauritius market uses stored codes/abbrev ("Fr"/"Mru"); localize to full FR names.
  MAURITIUS: "Maurice", MAURICE: "Maurice", MRU: "Maurice", MU: "Maurice",
  JAPAN: "Japon", JP: "Japon", IN: "Inde", AE: "Émirats arabes unis",
  "UNITED STATES": "États-Unis", "UNITED STATES OF AMERICA": "États-Unis", USA: "États-Unis",
  "UNITED KINGDOM": "Royaume-Uni", "GREAT BRITAIN": "Royaume-Uni",
  GERMANY: "Allemagne", SPAIN: "Espagne", ITALY: "Italie", CHINA: "Chine",
  PORTUGAL: "Portugal", NETHERLANDS: "Pays-Bas", BELGIUM: "Belgique",
  SWITZERLAND: "Suisse", CANADA: "Canada", BRAZIL: "Brésil", INDIA: "Inde",
  MOROCCO: "Maroc", TUNISIA: "Tunisie", GREECE: "Grèce", TURKEY: "Turquie",
  AUSTRIA: "Autriche", IRELAND: "Irlande", SWEDEN: "Suède", NORWAY: "Norvège",
  DENMARK: "Danemark", POLAND: "Pologne", RUSSIA: "Russie", MEXICO: "Mexique",
  THAILAND: "Thaïlande", "SOUTH KOREA": "Corée du Sud", "UNITED ARAB EMIRATES": "Émirats arabes unis",
  SINGAPORE: "Singapour", AUSTRALIA: "Australie", EGYPT: "Égypte", "SAUDI ARABIA": "Arabie saoudite",
};

export function localizeCountry(name) {
  if (!name) return name;
  const key = String(name).trim().toUpperCase();
  return COUNTRY_FR[key] || name;
}

export function localizeLabel(origin, destination) {
  return `${localizeCountry(origin)} → ${localizeCountry(destination)}`;
}

// Does a markets-data row match a canonical ORIGIN-DESTINATION focus key (e.g. "FR-MRU")?
// The Markets list is DESTINATION-scoped (the current org), so its rows are keyed by the ORIGIN
// market. We therefore match the full pair OR the origin segment against the row's key/iso.
// Display-only comparison — never mutates or duplicates data.
export function marketRowMatchesFocus(row, focusKey) {
  if (!row || !focusKey) return false;
  const origin = String(focusKey).split("-")[0].toUpperCase();
  if ([row.key, row.iso, row.air_market_key].some((v) => v && v === focusKey)) return true;
  return [row.key, row.iso, row.name].some((value) => {
    if (!value) return false;
    const normalized = String(value).trim().toUpperCase();
    return normalized === origin || isoFromCountry(normalized) === origin;
  });
}

// Full ISO 3166-1 alpha-2 table (English short names + common variants + FR aliases -> code).
// Generic: any recognised country maps automatically. "Monde"/"World" -> WORLD (FR-WORLD convention).
export const COUNTRY_ISO = {
  MONDE: "WORLD", WORLD: "WORLD",
  AFGHANISTAN: "AF", "ALAND ISLANDS": "AX", ALBANIA: "AL", ALGERIA: "DZ", "AMERICAN SAMOA": "AS",
  ANDORRA: "AD", ANGOLA: "AO", ANGUILLA: "AI", ANTARCTICA: "AQ", "ANTIGUA AND BARBUDA": "AG",
  ARGENTINA: "AR", ARMENIA: "AM", ARUBA: "AW", AUSTRALIA: "AU", AUSTRIA: "AT", AZERBAIJAN: "AZ",
  BAHAMAS: "BS", BAHRAIN: "BH", BANGLADESH: "BD", BARBADOS: "BB", BELARUS: "BY", BELGIUM: "BE",
  BELIZE: "BZ", BENIN: "BJ", BERMUDA: "BM", BHUTAN: "BT", BOLIVIA: "BO",
  "BONAIRE, SINT EUSTATIUS AND SABA": "BQ", "BOSNIA AND HERZEGOVINA": "BA", BOTSWANA: "BW",
  "BOUVET ISLAND": "BV", BRAZIL: "BR", "BRITISH INDIAN OCEAN TERRITORY": "IO",
  "BRUNEI DARUSSALAM": "BN", BRUNEI: "BN", BULGARIA: "BG", "BURKINA FASO": "BF", BURUNDI: "BI",
  "CABO VERDE": "CV", "CAPE VERDE": "CV", CAMBODIA: "KH", CAMEROON: "CM", CANADA: "CA",
  "CAYMAN ISLANDS": "KY", "CENTRAL AFRICAN REPUBLIC": "CF", CHAD: "TD", CHILE: "CL", CHINA: "CN",
  "CHRISTMAS ISLAND": "CX", "COCOS (KEELING) ISLANDS": "CC", COLOMBIA: "CO", COMOROS: "KM",
  CONGO: "CG", "CONGO, DEMOCRATIC REPUBLIC OF THE": "CD", "DR CONGO": "CD", "COOK ISLANDS": "CK",
  "COSTA RICA": "CR", "COTE D'IVOIRE": "CI", "IVORY COAST": "CI", CROATIA: "HR", CUBA: "CU",
  CURACAO: "CW", "CURAÇAO": "CW", CYPRUS: "CY", CZECHIA: "CZ", "CZECH REPUBLIC": "CZ",
  DENMARK: "DK", DJIBOUTI: "DJ", DOMINICA: "DM", "DOMINICAN REPUBLIC": "DO", ECUADOR: "EC",
  EGYPT: "EG", "EL SALVADOR": "SV", "EQUATORIAL GUINEA": "GQ", ERITREA: "ER", ESTONIA: "EE",
  ESWATINI: "SZ", SWAZILAND: "SZ", ETHIOPIA: "ET", "FALKLAND ISLANDS": "FK", "FAROE ISLANDS": "FO",
  FIJI: "FJ", FINLAND: "FI", FRANCE: "FR", "FRENCH GUIANA": "GF", "FRENCH POLYNESIA": "PF",
  "FRENCH SOUTHERN TERRITORIES": "TF", GABON: "GA", GAMBIA: "GM", GEORGIA: "GE", GERMANY: "DE",
  GHANA: "GH", GIBRALTAR: "GI", GREECE: "GR", GREENLAND: "GL", GRENADA: "GD", GUADELOUPE: "GP",
  GUAM: "GU", GUATEMALA: "GT", GUERNSEY: "GG", GUINEA: "GN", "GUINEA-BISSAU": "GW", GUYANA: "GY",
  HAITI: "HT", "HEARD ISLAND AND MCDONALD ISLANDS": "HM", "HOLY SEE": "VA", VATICAN: "VA",
  HONDURAS: "HN", "HONG KONG": "HK", HUNGARY: "HU", ICELAND: "IS", INDIA: "IN", INDONESIA: "ID",
  IRAN: "IR", "IRAN, ISLAMIC REPUBLIC OF": "IR", IRAQ: "IQ", IRELAND: "IE", "ISLE OF MAN": "IM",
  ISRAEL: "IL", ITALY: "IT", JAMAICA: "JM", JAPAN: "JP", JERSEY: "JE", JORDAN: "JO",
  KAZAKHSTAN: "KZ", KENYA: "KE", KIRIBATI: "KI", "KOREA, DEMOCRATIC PEOPLE'S REPUBLIC OF": "KP",
  "NORTH KOREA": "KP", "KOREA, REPUBLIC OF": "KR", "SOUTH KOREA": "KR", KUWAIT: "KW",
  KYRGYZSTAN: "KG", "LAO PEOPLE'S DEMOCRATIC REPUBLIC": "LA", LAOS: "LA", LATVIA: "LV",
  LEBANON: "LB", LESOTHO: "LS", LIBERIA: "LR", LIBYA: "LY", LIECHTENSTEIN: "LI", LITHUANIA: "LT",
  LUXEMBOURG: "LU", MACAO: "MO", MACAU: "MO", MADAGASCAR: "MG", MALAWI: "MW", MALAYSIA: "MY",
  MALDIVES: "MV", MALI: "ML", MALTA: "MT", "MARSHALL ISLANDS": "MH", MARTINIQUE: "MQ",
  MAURITANIA: "MR", MAURITIUS: "MU", MAYOTTE: "YT", MEXICO: "MX", MICRONESIA: "FM",
  MOLDOVA: "MD", "MOLDOVA, REPUBLIC OF": "MD", MONACO: "MC", MONGOLIA: "MN", MONTENEGRO: "ME",
  MONTSERRAT: "MS", MOROCCO: "MA", MOZAMBIQUE: "MZ", MYANMAR: "MM", BURMA: "MM", NAMIBIA: "NA",
  NAURU: "NR", NEPAL: "NP", NETHERLANDS: "NL", "NEW CALEDONIA": "NC", "NEW ZEALAND": "NZ",
  NICARAGUA: "NI", NIGER: "NE", NIGERIA: "NG", NIUE: "NU", "NORFOLK ISLAND": "NF",
  "NORTH MACEDONIA": "MK", MACEDONIA: "MK", "NORTHERN MARIANA ISLANDS": "MP", NORWAY: "NO",
  OMAN: "OM", PAKISTAN: "PK", PALAU: "PW", "PALESTINE, STATE OF": "PS", PALESTINE: "PS",
  PANAMA: "PA", "PAPUA NEW GUINEA": "PG", PARAGUAY: "PY", PERU: "PE", PHILIPPINES: "PH",
  PITCAIRN: "PN", POLAND: "PL", PORTUGAL: "PT", "PUERTO RICO": "PR", QATAR: "QA", REUNION: "RE",
  "RÉUNION": "RE", ROMANIA: "RO", "RUSSIAN FEDERATION": "RU", RUSSIA: "RU", RWANDA: "RW",
  "SAINT BARTHELEMY": "BL", "SAINT HELENA": "SH", "SAINT KITTS AND NEVIS": "KN",
  "SAINT LUCIA": "LC", "SAINT MARTIN": "MF", "SAINT PIERRE AND MIQUELON": "PM",
  "SAINT VINCENT AND THE GRENADINES": "VC", SAMOA: "WS", "SAN MARINO": "SM",
  "SAO TOME AND PRINCIPE": "ST", "SAUDI ARABIA": "SA", SENEGAL: "SN", SERBIA: "RS",
  SEYCHELLES: "SC", "SIERRA LEONE": "SL", SINGAPORE: "SG", "SINT MAARTEN": "SX", SLOVAKIA: "SK",
  SLOVENIA: "SI", "SOLOMON ISLANDS": "SB", SOMALIA: "SO", "SOUTH AFRICA": "ZA",
  "SOUTH GEORGIA": "GS", "SOUTH SUDAN": "SS", SPAIN: "ES", "SRI LANKA": "LK", SUDAN: "SD",
  SURINAME: "SR", "SVALBARD AND JAN MAYEN": "SJ", SWEDEN: "SE", SWITZERLAND: "CH",
  "SYRIAN ARAB REPUBLIC": "SY", SYRIA: "SY", TAIWAN: "TW", "TAIWAN, PROVINCE OF CHINA": "TW",
  TAJIKISTAN: "TJ", TANZANIA: "TZ", "TANZANIA, UNITED REPUBLIC OF": "TZ", THAILAND: "TH",
  "TIMOR-LESTE": "TL", "EAST TIMOR": "TL", TOGO: "TG", TOKELAU: "TK", TONGA: "TO",
  "TRINIDAD AND TOBAGO": "TT", TUNISIA: "TN", TURKEY: "TR", TURKIYE: "TR", "TÜRKIYE": "TR",
  TURKMENISTAN: "TM", "TURKS AND CAICOS ISLANDS": "TC", TUVALU: "TV", UGANDA: "UG",
  UKRAINE: "UA", "UNITED ARAB EMIRATES": "AE", UAE: "AE", "UNITED KINGDOM": "GB",
  "GREAT BRITAIN": "GB", UK: "GB", "UNITED STATES": "US", "UNITED STATES OF AMERICA": "US",
  USA: "US", "US MINOR OUTLYING ISLANDS": "UM", URUGUAY: "UY", UZBEKISTAN: "UZ", VANUATU: "VU",
  VENEZUELA: "VE", "VIET NAM": "VN", VIETNAM: "VN", "VIRGIN ISLANDS, BRITISH": "VG",
  "VIRGIN ISLANDS, U.S.": "VI", "WALLIS AND FUTUNA": "WF", "WESTERN SAHARA": "EH",
  YEMEN: "YE", ZAMBIA: "ZM", ZIMBABWE: "ZW",
  // French aliases (implicit destinations typed in FR)
  "ÉTATS-UNIS": "US", "ETATS-UNIS": "US", "ROYAUME-UNI": "GB", ALLEMAGNE: "DE", ESPAGNE: "ES",
  ITALIE: "IT", JAPON: "JP", CHINE: "CN", "PAYS-BAS": "NL", BELGIQUE: "BE", SUISSE: "CH",
  "BRÉSIL": "BR", INDE: "IN", MAROC: "MA", TUNISIE: "TN", "GRÈCE": "GR", TURQUIE: "TR",
  AUTRICHE: "AT", IRLANDE: "IE", "SUÈDE": "SE", "NORVÈGE": "NO", DANEMARK: "DK", POLOGNE: "PL",
  RUSSIE: "RU", MEXIQUE: "MX", "THAÏLANDE": "TH", "CORÉE DU SUD": "KR", "ÉMIRATS ARABES UNIS": "AE",
  SINGAPOUR: "SG", AUSTRALIE: "AU", "ÉGYPTE": "EG", "ARABIE SAOUDITE": "SA",
};

export function isoFromCountry(name) {
  if (!name) return null;
  return COUNTRY_ISO[String(name).trim().toUpperCase()] || null;
}

// Derive a market_key (e.g. FR-KE) from origin + destination. Returns null if either country
// is unknown/ambiguous (never invents a code -> publication stays blocked).
export function deriveMarketKey(origin, destination) {
  const o = isoFromCountry(origin);
  const d = isoFromCountry(destination);
  if (!o || !d) return null;
  return `${o}-${d}`;
}

// ADI market identities preserve the destination airport when the provider supplies one
// (FR-MRU, JP-MRU). Country-only files keep the ISO country convention (FR-JP).
export function deriveAdiMarketKey(origin, destination, destinationIata) {
  const o = isoFromCountry(origin);
  const iata = String(destinationIata || "").trim().toUpperCase();
  if (o && /^[A-Z]{3}$/.test(iata)) return `${o}-${iata}`;
  return deriveMarketKey(origin, destination);
}
